import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta } from '@angular/platform-browser';

declare global {
  interface Window {
    goatcounter?: { count?: (opts: { path: string; event?: boolean }) => void };
  }
}

export interface SoftwareAppJsonLd {
  name: string;
  description: string;
  url: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

export interface ServiceJsonLd {
  name: string;
  description: string;
  url: string;
  price: string;
  priceCurrency: string;
  areaServed?: string;
}

export interface ArticleJsonLd {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private meta = inject(Meta);
  private doc = inject<Document>(DOCUMENT);


  updateDescription(description: string): void {
    if (!description) return;
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'twitter:description', content: description });
  }

  updateTitleTags(title: string): void {
    if (!title) return;
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'twitter:title', content: title });
  }

  updateCanonical(url: string): void {
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
    this.meta.updateTag({ property: 'og:url', content: url });
  }

  updateSoftwareAppJsonLd(data: SoftwareAppJsonLd): void {
    this.doc.getElementById('page-service-jsonld')?.remove();
    this.doc.getElementById('page-article-jsonld')?.remove();

    const id = 'page-software-app-jsonld';
    let script = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: data.name,
      description: data.description,
      url: data.url,
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Any',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' }
    });
  }

  updateServiceJsonLd(data: ServiceJsonLd): void {
    this.doc.getElementById('page-software-app-jsonld')?.remove();
    this.doc.getElementById('page-article-jsonld')?.remove();

    const id = 'page-service-jsonld';
    let script = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: data.name,
      description: data.description,
      url: data.url,
      provider: { '@type': 'Organization', name: 'Every Rupee Spent', url: 'https://everyrupeespent.com' },
      areaServed: data.areaServed ?? 'IN',
      offers: { '@type': 'Offer', price: data.price, priceCurrency: data.priceCurrency }
    });
  }

  updateArticleJsonLd(data: ArticleJsonLd): void {
    this.doc.getElementById('page-software-app-jsonld')?.remove();
    this.doc.getElementById('page-service-jsonld')?.remove();

    const id = 'page-article-jsonld';
    let script = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: data.headline,
      description: data.description,
      url: data.url,
      datePublished: data.datePublished,
      author: { '@type': 'Organization', name: 'Every Rupee Spent', url: 'https://everyrupeespent.com' },
      publisher: { '@type': 'Organization', name: 'Every Rupee Spent', url: 'https://everyrupeespent.com' }
    });
  }

  updateFaqJsonLd(items: FaqItem[]): void {
    const id = 'page-faq-jsonld';
    const existing = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (items.length === 0) {
      existing?.remove();
      return;
    }
    const script = existing ?? this.doc.createElement('script');
    if (!existing) {
      script.id = id;
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    });
  }

  updateBreadcrumbJsonLd(items: BreadcrumbItem[]): void {
    const id = 'page-breadcrumb-jsonld';
    const existing = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (items.length < 2) {
      existing?.remove();
      return;
    }
    const script = existing ?? this.doc.createElement('script');
    if (!existing) {
      script.id = id;
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        item: item.url
      }))
    });
  }

  updateHowToJsonLd(name: string, steps: HowToStep[]): void {
    const id = 'page-howto-jsonld';
    const existing = this.doc.getElementById(id) as HTMLScriptElement | null;
    if (steps.length === 0) {
      existing?.remove();
      return;
    }
    const script = existing ?? this.doc.createElement('script');
    if (!existing) {
      script.id = id;
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name,
      step: steps.map(s => ({
        '@type': 'HowToStep',
        name: s.name,
        text: s.text
      }))
    });
  }

  trackPageview(path: string): void {
    this.doc.defaultView?.goatcounter?.count?.({ path });
  }

  trackEvent(name: string): void {
    this.doc.defaultView?.goatcounter?.count?.({ path: name, event: true });
  }
}
